import {
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { Check, MapPin, X } from 'lucide-react';
import {
  findFreeSlots,
  calcCourtBookingPrice,
  courtPricesSet,
  calcAssetBookingPrice,
  availableAssetsFor,
  courtDisplayName,
  BOOKING_DEFAULT_CONFIG,
  resolveBookingWindowDays,
  getEloLevel,
} from '../../../engine';
import { clubEffectiveCourts } from '../../../lib/clubCourts';
import { PayForBooking, paymentDueFor } from '../../booking/PayForBooking';
import { todayLocalISO, relativeDayLabel } from '../memberData';
import { Card, EmptyState, SectionTitle } from '../mobileUi';
import { useLocale, useT, type MessageKey } from '../../../i18n';
import { EquipmentAlone, bookableAssets } from './MobileBookEquipment';

/**
 * The equipment refusals the member command can answer with (#911 slice 2),
 * and the sentence each becomes. CODES on the wire, words here — the server
 * never sends a sentence. Anything unlisted is the generic one.
 */
const EQUIPMENT_REFUSAL: Record<string, MessageKey> = {
  asset_taken: 'mobile.book.equipment.error.taken',
  dead_gap: 'mobile.book.equipment.error.taken',
  asset_inactive: 'mobile.book.equipment.error.unavailable',
  asset_not_found: 'mobile.book.equipment.error.unavailable',
  outside_opening_hours: 'mobile.book.equipment.error.unavailable',
  off_grid: 'mobile.book.equipment.error.unavailable',
  asset_unpriceable: 'mobile.book.equipment.error.generic',
  asset_rejected: 'mobile.book.equipment.error.generic',
  invalid_assets: 'mobile.book.equipment.error.generic',
};

/**
 * The COURT refusals worded through the catalog. `court_prices_not_set` is the
 * club with no saved price zone (#1312); the sheet already disables «Book»
 * there, so this answers a stale screen — one loaded before the zones went.
 */
const COURT_REFUSAL: Record<string, MessageKey> = {
  court_prices_not_set: 'mobile.book.priceNotSetHint',
};
export interface MobileBookProps {
  member: any;
  bookings: any[];
  bookingConfig: any;
  /**
   * False until the club has SAVED its opening hours: every booking door
   * refuses `booking_hours_not_set` before that (owner decision 2026-10-31), so
   * the screen says so instead of offering slots from the engine's defaults.
   */
  bookingOpen?: boolean;
  bookingZones: any[];
  clubInfo: any;
  /** useBookingActions().addBooking */
  addBooking: (data: any, opts?: any) => Promise<any>;
  onBooked?: (booking: any) => void;
  /**
   * «Registrer en kamp på denne bookingen»: creates a planned single match on
   * the new booking with the member in the first slot and three open slots
   * other members can join under Matcher. Optional — hidden when absent.
   */
  onRegisterMatch?: (
    booking: any,
    opts: { levelMin: number | null; levelMax: number | null },
  ) => Promise<any>;
  /** Inside a centre page: no heading. */
  embedded?: boolean;
  /**
   * The venue's bookable equipment and when each machine is taken, as
   * `/member/bootstrap` projects them (#911 slice 2). Empty — or absent — and
   * the screen offers none, exactly as before.
   */
  assets?: any[];
  assetBookings?: any[];
  /**
   * THE MEMBER EQUIPMENT COMMAND (#911 slice 2, § 6b) — a machine booked on
   * its own, from the «Utstyr» mode. `useAssets().addMemberAssetBooking` on the
   * player surface. Absent, and the mode still shows but cannot book.
   */
  addAssetBooking?: (intent: any) => Promise<any>;
}

// «Book» — one screen, no detours: pick a day and duration, tap a start
// time (the court list below flips to Ledig/Opptatt for that slot), tap a
// free court, and a sheet at the bottom shows time · court · price with the
// booking button. Reuses the booking engine untouched: findFreeSlots for
// availability, calcCourtBookingPrice for the price, and addBooking
// (validateBooking inside) to actually create it.
export function MobileBook({
  member,
  bookings,
  bookingConfig,
  bookingOpen = true,
  bookingZones,
  clubInfo,
  addBooking,
  onBooked,
  onRegisterMatch,
  embedded = false,
  assets = [],
  assetBookings = [],
  addAssetBooking,
}: MobileBookProps) {
  const { t, formatDate } = useLocale();
  // The machines ticked beside the chosen court (#911 slice 2), by asset id.
  const [chosenAssets, setChosenAssets] = useState<string[]>([]);
  // What the last booking carried, for its confirmation.
  const [doneEquipment, setDoneEquipment] = useState<string[]>([]);
  // WHAT IS BEING BOOKED (§ 6b): a court — with equipment ticked onto it — or
  // a piece of equipment on its own. The second mode exists only where the
  // venue offers equipment, so a club without it never sees a toggle.
  const offeredEquipment = bookableAssets(assets);
  const [mode, setMode] = useState<'court' | 'equipment'>('court');
  const [registerMatch, setRegisterMatch] = useState(false);
  // Optional level gate for the open slots (null = anyone can join).
  const [levelMin, setLevelMin] = useState<number | null>(null);
  const [levelMax, setLevelMax] = useState<number | null>(null);
  // Recommended gate = the member's own level ± 1 (clamped to N1–N9); applied
  // when the box is ticked, and available again via «Bruk anbefalt».
  const myLevel = getEloLevel(Math.round(member?.elo || 0)).level;
  const recMin = Math.max(1, myLevel - 1);
  const recMax = Math.min(9, myLevel + 1);
  const applyRecommended = () => {
    setLevelMin(recMin);
    setLevelMax(recMax);
  };
  const [matchNote, setMatchNote] = useState<string | null>(null);
  // Ties the level selects to their own heading. `useId` rather than a constant:
  // a centre page can render this screen more than once.
  const levelGateId = useId();
  // The disclosure's accessible name is built from its own content AND the
  // price beside it — see the comment at the markup.
  const summaryId = useId();
  const detailId = useId();
  const priceId = useId();
  // The booking sheet can be minimised to one line (tap the handle / drag it
  // down) so the court list behind it stays reachable.
  const [collapsed, setCollapsed] = useState(false);
  const dragStartY = useRef<number | null>(null);
  const onHandlePointerDown = (e: ReactPointerEvent) => {
    dragStartY.current = e.clientY;
  };
  const onHandlePointerUp = (e: ReactPointerEvent) => {
    const start = dragStartY.current;
    dragStartY.current = null;
    if (start == null) return;
    const dy = e.clientY - start;
    if (dy > 24) setCollapsed(true);
    else if (dy < -24) setCollapsed(false);
    else setCollapsed((c) => !c); // a plain tap toggles
  };
  // THE HANDLE WAS POINTER-ONLY, so it was inoperable from the keyboard
  // (WCAG 2.1.1, Level A): all of the toggle lives in `onPointerUp`, and a
  // keyboard activation of a <button> dispatches `click`, which nothing here
  // listened for. The fix is a KEY handler rather than an `onClick`, on
  // purpose: a pointer tap fires `pointerup` AND then `click`, so moving the
  // toggle to `onClick` — or adding one beside the pointer handlers — would
  // make a single tap toggle twice. `preventDefault()` stops the browser
  // synthesising the click that Space/Enter on a button would otherwise send.
  const onHandleKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    setCollapsed((c) => !c);
  };
  const cfg = { ...BOOKING_DEFAULT_CONFIG, ...(bookingConfig || {}) };
  const totalCourts = Number(cfg.courtCount) || Number(clubInfo?.courts) || 4;
  // The shared answer. This file already had the COUNT right — it is the only
  // one that consulted `courtCount` — but it passed `enabledCourts: null` into
  // the engine, which then counted 1..totalCourts itself and reintroduced any
  // court the admin had closed. One function now answers both halves.
  //
  // WITH the physical fallback (#873): handed `clubInfo?.courts` bare, a club
  // that had never stored a count got slots on courts 1–4 (from `totalCourts`
  // above) and no court rows to pick them on.
  const effectiveCourts = clubEffectiveCourts(cfg, clubInfo);
  const courtMeta = cfg.courtMeta || {};
  const durations: number[] =
    Array.isArray(cfg.allowedDurations) && cfg.allowedDurations.length
      ? cfg.allowedDurations
      : [60, 90, 120];

  // THE CLUB'S WINDOW, not a number typed here (#234). This used to build a
  // fixed fourteen days, so a club that set the window to 7 still had members
  // booking a fortnight out and the setting did nothing on the one surface
  // members actually use. The server enforces the same resolver.
  const windowDays = resolveBookingWindowDays(cfg);
  const days = useMemo(() => {
    const out: string[] = [];
    const d = new Date();
    for (let i = 0; i < windowDays; i++) {
      out.push(todayLocalISO(d));
      d.setDate(d.getDate() + 1);
    }
    return out;
  }, [windowDays]);

  const [date, setDateState] = useState(days[0]);
  // 'any' = no fixed duration: the grid shows every start time with a free
  // court, and the duration is chosen per court once you tap it.
  const [duration, setDurationState] = useState<number | 'any'>('any');
  // Selection lives in the overview: a start time, then a court (and, in
  // 'any' mode, a duration for that court).
  const [selTime, setSelTime] = useState<string | null>(null);
  const [courtSel, setCourtSel] = useState<number | null>(null);
  const [pick, setPick] = useState<{
    startTime: string;
    endTime: string;
    court: number;
    durationMin: number;
  } | null>(null);
  const setDate = (d: string) => {
    setSelTime(null);
    setCourtSel(null);
    setPick(null);
    setDateState(d);
  };
  const setDuration = (m: number | 'any') => {
    setSelTime(null);
    setCourtSel(null);
    setPick(null);
    setDurationState(m);
  };
  /**
   * THE BOOKING WE ARE PAYING FOR (#284), or null.
   *
   * It used to be a boolean, because the old flow paid BEFORE the booking
   * existed: a mock terminal, then `addBooking` claiming `paymentStatus: 'paid'`.
   * For a member that claim was already being discarded — `/member/bookings`
   * derives price and status itself — so the receipt changed nothing server-side.
   *
   * Now the booking is created first and this holds the row being paid for, which
   * is what the server requires and what makes an abandoned payment harmless: a
   * pending booking whose court is still held.
   */
  /**
   * Whether to open the payment sheet straight after booking.
   *
   * It used to hold `{ id, summary }` and be the ONLY way to the sheet — so closing
   * it stranded a pending booking with no way back. `PayForBooking` now owns the
   * sheet and lives on the confirmation as well as in the reservations list, so this
   * is reduced to «did we just book something that owes money».
   */
  const [payNowFor, setPayNowFor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<any>(null);

  // Free slots per allowed duration (one engine call each) — the grid uses the
  // chosen duration, or the shortest one in 'any' mode (a superset of the rest).
  const slotsByDuration = useMemo(
    () =>
      Object.fromEntries(
        durations.map((d) => [
          d,
          findFreeSlots(bookings || [], date, totalCourts, d, {
            openTime: cfg.openTime,
            closeTime: cfg.closeTime,
            bufferMin: cfg.bufferMin,
            stepMin: cfg.stepMin,
            smartAllocation: cfg.smartAllocation,
            enabledCourts: effectiveCourts,
            // THE SHORTEST ALLOWED DURATION, the floor validation uses (#873).
            // This read `cfg.minBookingMin`, which the booking settings never
            // write, so with smart allocation off a slot leaving a gap shorter
            // than any booking was offered and then refused by the server.
            // BookingView's slot list has always passed the shortest duration.
            minBookingMin: Math.min(...durations),
            allowedDurations: durations,
          }),
        ]),
      ) as Record<number, any[]>,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bookings, date, totalCourts],
  );
  const baseDur = duration === 'any' ? Math.min(...durations) : duration;
  const slots = slotsByDuration[baseDur] || [];
  /** Durations that are actually bookable on court c from startTime. */
  const durationsFor = (c: number, startTime: string): number[] =>
    durations.filter((d) =>
      (slotsByDuration[d] || [])
        .find((s: any) => s.startTime === startTime)
        ?.freeBaneList?.includes(c),
    );

  // Hide slots that have already started today.
  const nowHHMM = (() => {
    const n = new Date();
    return `${String(n.getHours()).padStart(2, '0')}:${String(n.getMinutes()).padStart(2, '0')}`;
  })();
  const visibleSlots = slots.filter((s: any) => date !== days[0] || s.startTime > nowHHMM);

  // ── NO PRICE UNTIL ZONES EXIST (#1312, owner decision 2026-11-03) ──
  //
  // The SAME engine rule the server prices with (`courtPricesSet`): a club with
  // no saved zone has no court price, and `/member/bookings` refuses
  // `court_prices_not_set` there. So the sheet says «Pris ikke satt» and does
  // not offer «Book» — never the default rate, never «free».
  const pricesSet = courtPricesSet(bookingZones);
  const price = pick
    ? calcCourtBookingPrice(bookingZones, date, pick.startTime, pick.endTime, cfg)?.total || 0
    : 0;

  // ── EQUIPMENT BESIDE THE COURT (#911 slice 2) ──────────────────────
  //
  // What the server would accept for the chosen court's whole time, through the
  // SAME engine and the SAME inputs it validates and prices with: the stored
  // booking configuration as it is (`bookingConfig`, not merged with defaults),
  // because the server reads the stored blob for equipment (`rulesFor`,
  // `calcAssetBookingPrice`). So the price a member is shown is the price they
  // are charged, and a machine offered here is one the server will book —
  // unless somebody takes it first, which the server answers with a code.
  const storedCfg = bookingConfig || {};
  const hours =
    typeof storedCfg.openTime === 'string' && typeof storedCfg.closeTime === 'string'
      ? { openTime: storedCfg.openTime, closeTime: storedCfg.closeTime }
      : {};
  const freeEquipment: any[] = pick
    ? availableAssetsFor(
        assets,
        assetBookings,
        { date, startTime: pick.startTime, endTime: pick.endTime },
        hours,
      )
    : [];
  const equipmentPrice = (a: any): number =>
    pick ? calcAssetBookingPrice(a, date, pick.startTime, pick.endTime, storedCfg).total || 0 : 0;
  // Only what is still free counts: a machine that stopped being free since it
  // was ticked is not sent, and not added to the total.
  const ticked = freeEquipment.filter((a) => chosenAssets.includes(a.id));
  const total = price + ticked.reduce((sum, a) => sum + equipmentPrice(a), 0);

  const finish = async (receipt: any | null) => {
    if (!pick) return;
    const slot = pick;
    setBusy(true);
    setError(null);
    // ALWAYS RELEASED (#1145). A booking that threw used to leave the button on
    // «Booker …» for good, with nothing on screen saying why.
    let res: any;
    try {
      res = await addBooking({
        court: slot.court,
        date,
        startTime: slot.startTime,
        endTime: slot.endTime,
        durationMin: slot.durationMin,
        bookedBy: {
          name: member?.name || '',
          email: member?.email || '',
          phone: member?.phone || '',
        },
        notes: '',
        // THE MACHINES, by id (#911). Priced and booked by the server in the same
        // command as the court, and paid for in the court's one payment.
        ...(ticked.length ? { assetIds: ticked.map((a) => a.id) } : {}),
        // PASSED FOR THE CLUB PATH ONLY. `/member/bookings` ignores both and derives
        // them from the club's own zones, which is why the old mock receipt never
        // changed anything server-side. Left as they were rather than removed: this
        // same handler serves the non-member path, and narrowing it is not this
        // slice's business.
        paymentStatus: receipt ? 'paid' : 'free',
        priceNok: price,
        source: 'user',
      });
    } catch {
      res = { success: false, error: t('mobile.book.failed') };
    } finally {
      setBusy(false);
    }
    if (res?.success) {
      setDone(res.booking);
      setDoneEquipment(
        (res.assetBookings || []).map(
          (ab: any) => assets.find((a) => a.id === ab.assetId)?.name || '',
        ),
      );
      setChosenAssets([]);
      setPick(null);
      setSelTime(null);
      setCourtSel(null);
      onBooked?.(res.booking);

      // ── PAY FOR WHAT THE SERVER SAYS IS OWED ─────────────────────
      //
      // `pending` is the server's word for «priced and not paid» — it derives the
      // price from the club's zones, so this does not decide whether money is owed
      // and cannot be talked into skipping it. A `free` booking is simply done.
      //
      // If the member closes the sheet, the booking stays pending with its court
      // held; nothing has to be unwound and they can pay from their bookings later.
      if (paymentDueFor(res.booking)) setPayNowFor(res.booking.id);
      // The match is a side-channel, like the desktop: the booking never fails
      // because the plan did.
      setMatchNote(null);
      if (registerMatch && onRegisterMatch && res.booking?.id) {
        try {
          const r = await onRegisterMatch(res.booking, { levelMin, levelMax });
          setMatchNote(
            r?.success === false
              ? t('mobile.book.matchNotRegistered')
              : t('mobile.book.matchPosted'),
          );
        } catch {
          setMatchNote(t('mobile.book.matchNotRegistered'));
        }
      }
    } else {
      // An equipment refusal is a CODE the catalog words; everything else keeps
      // the sentence the member path has always shown.
      const equipmentKey = res?.code
        ? (EQUIPMENT_REFUSAL[res.code] ?? COURT_REFUSAL[res.code])
        : undefined;
      setError(equipmentKey ? t(equipmentKey) : res?.error || t('mobile.book.failed'));
    }
  };

  const confirm = () => {
    if (!pick || !pricesSet) return;
    // ALWAYS BOOK FIRST. `finish` opens the payment sheet itself when the created
    // booking turns out to be priced — the server decides that, not this screen.
    void finish(null);
  };

  // `.replace('.', '')` survives the locale change on purpose: nb-NO's short
  // month carries a trailing period («jan.») and the chip is built for three
  // characters. en-GB has none, so the strip is a no-op there.
  const monthLabel = formatDate(new Date(date + 'T00:00:00'), {
    month: 'short',
    year: 'numeric',
  })
    .replace('.', '')
    .toUpperCase();
  const courts = effectiveCourts;
  const selSlot = selTime ? visibleSlots.find((s: any) => s.startTime === selTime) || null : null;
  const freeCourts: number[] = selSlot ? selSlot.freeBaneList || [] : [];
  const pickWithDuration = (c: number, d: number) => {
    if (!selSlot) return;
    const slot = (slotsByDuration[d] || []).find((s: any) => s.startTime === selSlot.startTime);
    if (!slot) return;
    setError(null);
    setCollapsed(false);
    // A new court or length is a new choice of equipment too.
    setChosenAssets([]);
    setPick({ startTime: slot.startTime, endTime: slot.endTime, court: c, durationMin: d });
  };
  const chooseCourt = (c: number) => {
    if (!selSlot) return;
    if (duration === 'any') {
      // The row's duration track is where the length is chosen; one possible
      // length books straight away, otherwise the row just asks you to pick.
      const opts = durationsFor(c, selSlot.startTime);
      setPick(null);
      if (opts.length === 1) pickWithDuration(c, opts[0]);
      else setCourtSel(courtSel === c ? null : c);
      return;
    }
    pickWithDuration(c, duration);
  };

  const heading = !embedded && (
    <h1
      className="font-display font-semibold text-ink leading-tight pt-2 @wide:col-span-2 @wide:row-start-1"
      style={{ fontSize: 26 }}
    >
      {t('mobile.home.bookCourt')}
    </h1>
  );

  // NOT OPEN YET (owner decision 2026-10-31): no slots, no equipment — the
  // server would refuse every one of them.
  if (!bookingOpen) {
    return (
      <div className="px-4 pb-4 space-y-3">
        {heading}
        <EmptyState title={t('mobile.book.notOpen')} hint={t('mobile.book.notOpenHint')} />
      </div>
    );
  }

  return (
    // ── ON A WIDE SCREEN: PICK ON THE LEFT, COURTS ON THE RIGHT ────────
    // (desktop step 3). The day, the duration and the free start times stay a
    // compact column; the courts — what a chosen time leaves free — get the
    // width beside them, with the booking sheet under them. PLACED by the
    // grid; the markup order, and so the phone, is unchanged.
    <div className="px-4 pb-4 space-y-3 @wide:space-y-0 @wide:grid @wide:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] @wide:[grid-template-rows:auto_auto_auto_auto_1fr_auto] @wide:gap-x-8 @wide:gap-y-3 @wide:items-start">
      {heading}

      {/* Calendar strip */}
      <div className="@wide:col-start-1 @wide:row-start-2">
        {/* WHAT TO BOOK — only where the venue offers equipment (§ 6b). */}
        {offeredEquipment.length > 0 && (
          <div
            className="flex gap-1.5 mb-2"
            role="group"
            aria-label={t('mobile.book.equipment.alone.modeAria')}
          >
            {(['court', 'equipment'] as const).map((m) => {
              const active = m === mode;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    setMode(m);
                    setPick(null);
                    setChosenAssets([]);
                    setSelTime(null);
                    setCourtSel(null);
                  }}
                  aria-pressed={active}
                  className="rounded-full px-3 py-1 text-[12px] font-semibold"
                  style={{
                    background: active ? '#c2f02f' : '#fff',
                    color: '#0f1611',
                    border: `1px solid ${active ? '#a3d117' : '#e7eae3'}`,
                  }}
                >
                  {m === 'court'
                    ? t('mobile.book.equipment.alone.modeCourt')
                    : t('mobile.book.equipment.alone.modeEquipment')}
                </button>
              );
            })}
          </div>
        )}
        <div className="font-mono text-[10px] tracking-wider text-ink-3 px-1 mb-1">
          {monthLabel}
        </div>
        <div className="flex gap-1 overflow-x-auto -mx-4 px-4 pb-1 scrollbar-thin">
          {days.map((d) => {
            const active = d === date;
            const [, , dd] = d.split('-');
            const wd = formatDate(new Date(d + 'T00:00:00'), { weekday: 'short' })
              .replace('.', '')
              .slice(0, 1)
              .toUpperCase();
            return (
              <button
                key={d}
                onClick={() => setDate(d)}
                className="flex-shrink-0 flex flex-col items-center"
                style={{ width: 44 }}
                aria-label={relativeDayLabel(d)}
                aria-pressed={active}
              >
                <span className="font-mono text-[10px] text-ink-3">{wd}</span>
                <span
                  className="flex items-center justify-center rounded-full font-semibold text-[15px] mt-0.5 tabular-nums"
                  style={{
                    width: 36,
                    height: 36,
                    background: active ? '#c2f02f' : 'transparent',
                    color: '#0f1611',
                  }}
                >
                  {Number(dd)}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Duration — «Alle» shows every free start and asks per court */}
      <div className="flex gap-1.5 @wide:col-start-1 @wide:row-start-3">
        {(['any', ...durations] as (number | 'any')[]).map((m) => {
          const active = m === duration;
          return (
            <button
              key={m}
              onClick={() => setDuration(m)}
              aria-pressed={active}
              className="rounded-full px-3 py-1 text-[12px] font-semibold"
              style={{
                background: active ? '#0f1611' : '#fff',
                color: active ? '#fff' : '#0f1611',
                border: `1px solid ${active ? '#0f1611' : '#e7eae3'}`,
              }}
            >
              {m === 'any' ? t('mobile.book.anyLength') : `${m} min`}
            </button>
          );
        })}
      </div>

      {/* EQUIPMENT ON ITS OWN (§ 6b): the same day and length, a machine in the
          court's place. Everything below the chips is its own. */}
      {mode === 'equipment' && (
        <EquipmentAlone
          member={member}
          assets={offeredEquipment}
          assetBookings={assetBookings}
          cfg={storedCfg}
          date={date}
          isToday={date === days[0]}
          durations={durations}
          duration={duration}
          addAssetBooking={addAssetBooking}
        />
      )}

      {mode === 'court' && (
        <>
          {/* Start times — dense grid, time only */}
          <div className="@wide:col-start-1 @wide:row-start-4">
            <SectionTitle>
              {t('mobile.book.freeTimes', { day: relativeDayLabel(date) })}
            </SectionTitle>
            {visibleSlots.length === 0 ? (
              <EmptyState title={t('mobile.book.noTimes')} hint={t('mobile.book.noTimesHint')} />
            ) : (
              <div className="grid grid-cols-4 gap-1.5">
                {visibleSlots.map((s: any) => {
                  const few = s.bookableCourts === 1;
                  const active = s.startTime === selTime;
                  return (
                    <button
                      key={s.startTime}
                      onClick={() => {
                        setPick(null);
                        setCourtSel(null);
                        setSelTime(active ? null : s.startTime);
                      }}
                      aria-pressed={active}
                      className="rounded-xl py-2 text-center active:scale-[0.97] transition-transform"
                      style={{
                        background: active ? '#0f1611' : '#fff',
                        color: active ? '#fff' : '#0f1611',
                        border: `1px solid ${active ? '#0f1611' : few ? '#fde68a' : '#e7eae3'}`,
                      }}
                      title={
                        s.bookableCourts === 1
                          ? t('mobile.book.courtFree', { count: s.bookableCourts })
                          : t('mobile.book.courtsFree', { count: s.bookableCourts })
                      }
                    >
                      <div className="text-[14px] font-semibold tabular-nums">{s.startTime}</div>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="text-[10px] text-ink-3 px-1 mt-1.5">
              {selSlot
                ? duration === 'any'
                  ? t('mobile.book.pickCourtOpen', { time: selSlot.startTime })
                  : t('mobile.book.pickCourtFixed', {
                      from: selSlot.startTime,
                      to: selSlot.endTime,
                    })
                : t('mobile.book.pickTimeFirst')}
            </div>
          </div>

          {/* Courts — the place you pick a court. With a time chosen, only the free ones. */}
          <div className="@wide:col-start-2 @wide:row-start-2 @wide:row-span-4">
            <SectionTitle>
              {selSlot
                ? duration === 'any'
                  ? t('mobile.book.freeCourtsAt', { time: selSlot.startTime })
                  : t('mobile.book.freeCourtsBetween', {
                      from: selSlot.startTime,
                      to: selSlot.endTime,
                    })
                : t('mobile.centre.courts')}
            </SectionTitle>
            <div className="space-y-1.5">
              {selSlot && freeCourts.length === 0 && (
                <EmptyState
                  title={t('mobile.book.noCourts')}
                  hint={t('mobile.book.noCourtsHint')}
                />
              )}
              {courts.map((c) => {
                const freeAt = visibleSlots.filter((s: any) => s.freeBaneList?.includes(c)).length;
                const freeNow = selSlot ? freeCourts.includes(c) : null;
                if (freeNow === false) return null;
                const chosen = pick?.court === c;
                const opts =
                  selSlot && duration === 'any' ? durationsFor(c, selSlot.startTime) : [];
                const expanded = courtSel === c && duration === 'any' && selSlot != null;
                return (
                  <Card
                    key={c}
                    className="!py-2.5"
                    onClick={
                      freeNow && !expanded && !(selSlot && duration === 'any')
                        ? () => chooseCourt(c)
                        : undefined
                    }
                    accent={chosen || expanded ? '#0f1611' : freeNow ? '#c2f02f' : undefined}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="flex items-center justify-center rounded-xl flex-shrink-0 font-display font-semibold"
                        style={{
                          width: 36,
                          height: 36,
                          background: chosen ? '#0f1611' : '#eef0ea',
                          color: chosen ? '#fff' : '#0f1611',
                          fontSize: 14,
                        }}
                      >
                        {chosen ? <Check size={16} strokeWidth={2.5} /> : c}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-semibold text-ink">
                          {courtDisplayName(c, courtMeta)}
                        </div>
                        <div className="text-[11px] text-ink-3">{t('mobile.centre.courtKind')}</div>
                      </div>
                      <div
                        className="font-mono text-[10px] flex-shrink-0 font-bold"
                        style={{ color: freeNow ? '#3f6212' : 'var(--ink-3)' }}
                      >
                        {selSlot
                          ? freeNow
                            ? chosen
                              ? t('mobile.book.chosen')
                              : duration === 'any'
                                ? expanded
                                  ? t('mobile.book.pickLength')
                                  : opts.length === 1
                                    ? t('mobile.book.lengthCount', { count: opts.length })
                                    : t('mobile.book.lengthCountPlural', { count: opts.length })
                                : t('mobile.book.freeBookIt')
                            : t('mobile.book.taken')
                          : freeAt === 1
                            ? t('mobile.book.timeCount', { count: freeAt })
                            : t('mobile.book.timeCountPlural', { count: freeAt })}
                      </div>
                    </div>
                    {selSlot && duration === 'any' && freeNow && (
                      <DurationTrack
                        durations={durations}
                        available={opts}
                        startTime={selSlot.startTime}
                        chosen={chosen ? pick!.durationMin : null}
                        endTimeFor={(d) =>
                          (slotsByDuration[d] || []).find(
                            (s: any) => s.startTime === selSlot.startTime,
                          )?.endTime || null
                        }
                        priceFor={(d) => {
                          const slot = (slotsByDuration[d] || []).find(
                            (s: any) => s.startTime === selSlot.startTime,
                          );
                          // Null — no price shown — when the club has none (#1312).
                          if (!pricesSet) return null;
                          return slot
                            ? calcCourtBookingPrice(
                                bookingZones,
                                date,
                                slot.startTime,
                                slot.endTime,
                                cfg,
                              )?.total || 0
                            : 0;
                        }}
                        onPick={(d) => {
                          setCourtSel(null);
                          pickWithDuration(c, d);
                        }}
                      />
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Bottom sheet: time · court · price · book. Sticks to the bottom of the screen. */}
      {mode === 'court' && (pick || done) && (
        <div
          className="sticky bottom-0 -mx-4 px-3 pt-2 @wide:col-start-2 @wide:row-start-6 @wide:mx-0 @wide:px-0"
          style={{ zIndex: 10 }}
        >
          <div
            className="rounded-3xl px-4 pt-3 pb-4"
            style={{
              background: '#0f1611',
              color: '#fff',
              boxShadow: '0 -8px 30px rgba(15,22,17,0.25)',
            }}
            // A REGION, NOT A DIALOG (#900). It sits at the bottom of the page
            // and the page stays usable around it — nothing is modal, nothing
            // takes focus, nothing closes on Escape — so `role="dialog"`
            // promised a behaviour it never had. A named region is what it is:
            // reachable as a landmark, announced by the same name.
            role="region"
            aria-label={done ? t('mobile.book.confirmedAria') : t('mobile.home.bookCourt')}
          >
            {/* ≥24px TALL (WCAG 2.5.8) WITHOUT MOVING A PIXEL. The grab bar is
                4px, so `py-1 -mt-1 mb-1` gave a 12px target. The padding grows
                to 10px and the margins give those 6px back on each side, so the
                bar and everything under it stay exactly where they were:
                -10 + 10 + 4 + 10 - 2 = 12px of layout, as before, around a
                24px-tall box. */}
            <button
              type="button"
              onPointerDown={onHandlePointerDown}
              onPointerUp={onHandlePointerUp}
              onKeyDown={onHandleKeyDown}
              aria-label={collapsed ? t('mobile.book.expand') : t('mobile.book.collapse')}
              aria-expanded={!collapsed}
              className="block w-full py-2.5 -mt-[10px] -mb-[2px] cursor-grab active:cursor-grabbing"
              style={{ touchAction: 'none' }}
            >
              <span
                className="block mx-auto rounded-full"
                style={{ width: 36, height: 4, background: 'rgba(255,255,255,0.35)' }}
              />
            </button>
            {done ? (
              <>
                <div className="flex items-center gap-3">
                  <div
                    className="flex items-center justify-center rounded-full flex-shrink-0"
                    style={{ width: 40, height: 40, background: '#c2f02f', color: '#0f1611' }}
                  >
                    <Check size={20} strokeWidth={2.5} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold" style={{ fontSize: 18 }}>
                      {/* NOT «banen er din» while it still owes money. The court IS
                          held, and saying only that would send the member away from
                          the one screen that can take the payment. */}
                      {paymentDueFor(done)
                        ? t('mobile.book.courtHeldUnpaid')
                        : t('mobile.book.courtIsYours')}
                    </div>
                    <div className="text-[12px] opacity-80 flex items-center gap-1">
                      <MapPin size={12} /> {courtDisplayName(done.court, courtMeta)} ·{' '}
                      {relativeDayLabel(done.date)} {done.startTime}–{done.endTime}
                    </div>
                    {doneEquipment.length > 0 && (
                      <div className="text-[12px] opacity-80">
                        {t('mobile.book.equipment.added', {
                          names: doneEquipment.filter(Boolean).join(', '),
                        })}
                      </div>
                    )}
                    {matchNote && <div className="text-[11px] mt-1 opacity-80">{matchNote}</div>}
                    {/* THE WAY BACK TO PAYMENT, on the booking rather than on the
                        flow — and the same component the reservations list uses, so
                        closing the sheet is recoverable from either place. */}
                    <div className="mt-2">
                      <PayForBooking
                        booking={done}
                        summary={`${courtDisplayName(done.court, courtMeta)} · ${relativeDayLabel(done.date)} ${done.startTime}–${done.endTime}`}
                        autoOpen={payNowFor === done.id}
                        // THE SERVER SAID PAID, so this confirmation stops saying
                        // «betal for å fullføre» — it is the same booking and the
                        // heading above reads `paymentStatus`. Local to this screen's
                        // own copy: nothing is written anywhere and the status came
                        // from the server. The store's copy is still `pending` until
                        // the bootstrap next refreshes (on return to the app, #1106);
                        // until then the reservations list's PayForBooking re-reads
                        // the status itself and corrects its row.
                        onPaid={() => setDone({ ...done, paymentStatus: 'paid' })}
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => setDone(null)}
                    aria-label={t('mobile.common.close')}
                    className="p-1 opacity-70"
                  >
                    <X size={18} />
                  </button>
                </div>
              </>
            ) : pick && collapsed ? (
              /* TWO CONTROLS SIDE BY SIDE, not one inside the other. «Book» was
                 a <span role="button" onClick> NESTED in the expand <button>:
                 invalid HTML, and it worked for a pointer only because of the
                 `stopPropagation` it needed. It had no `tabIndex`, so Tab
                 landed on the outer button and EXPANDED the sheet instead of
                 booking — the one action a member came here for was
                 unreachable from a keyboard (WCAG 2.1.1). Siblings now, and the
                 expand button keeps its own visible content as its accessible
                 name rather than an `aria-label` that replaced it. */
              <div className="w-full flex items-center justify-between gap-3">
                {/* ── THE PRICE IS PART OF THE NAME (#816 review) ────────
                    Splitting «Book» out of the disclosure left the price in a
                    SIBLING span, outside both buttons. So a screen-reader user
                    navigating by control heard the time and court from the
                    disclosure, «Book» from the action, and the cost from
                    NEITHER — they had to find it as loose text in between.

                    `aria-labelledby` over moving the DOM: the price has to stay
                    where it is visually, and listing the ids concatenates their
                    text into the disclosure's name without touching the layout.

                    EVERY LINE IS LISTED, and that is not tidiness. The first
                    version referenced only the headline and the price, and
                    because `aria-labelledby` REPLACES descendant content rather
                    than adding to it, that silently dropped the day and the
                    duration from the name — trading a missing price for a
                    missing «i morgen», which in a court booking is the detail
                    somebody most needs to hear. Caught in the #816 re-review;
                    my own test had checked time, court and price and so passed
                    straight over it. */}
                <button
                  type="button"
                  onClick={() => setCollapsed(false)}
                  aria-expanded={!collapsed}
                  aria-labelledby={`${summaryId} ${detailId} ${priceId}`}
                  className="min-w-0 flex-1 text-left"
                >
                  <div
                    id={summaryId}
                    className="font-display font-semibold leading-tight"
                    style={{ fontSize: 16 }}
                  >
                    {pick.startTime}–{pick.endTime} · {courtDisplayName(pick.court, courtMeta)}
                  </div>
                  <div id={detailId} className="text-[11px] opacity-70">
                    {relativeDayLabel(date)} · {pick.durationMin} min
                    {registerMatch ? t('mobile.book.withMatchSuffix') : ''}
                  </div>
                </button>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span
                    id={priceId}
                    className="font-display font-semibold"
                    style={{ fontSize: 18 }}
                  >
                    {!pricesSet
                      ? t('mobile.book.priceNotSet')
                      : total > 0
                        ? `${total} kr`
                        : t('mobile.activities.free')}
                  </span>
                  <button
                    type="button"
                    onClick={() => confirm()}
                    disabled={!pricesSet}
                    className="rounded-xl px-3 py-2 text-[12px] font-semibold disabled:opacity-40"
                    style={{ background: '#c2f02f', color: '#0f1611' }}
                  >
                    {t('mobile.book.bookShort')}
                  </button>
                </div>
              </div>
            ) : pick ? (
              <>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-mono text-[10px] uppercase tracking-wider opacity-60">
                      {relativeDayLabel(date)} · {pick.durationMin} min
                    </div>
                    <div
                      className="font-display font-semibold leading-tight"
                      style={{ fontSize: 20 }}
                    >
                      {pick.startTime}–{pick.endTime}
                    </div>
                    <div className="text-[12px] opacity-80 flex items-center gap-1 mt-0.5">
                      <MapPin size={12} />{' '}
                      {t('mobile.book.bookedFor', {
                        court: courtDisplayName(pick.court, courtMeta),
                        name: member?.name || t('mobile.book.you'),
                      })}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-display font-semibold" style={{ fontSize: 22 }}>
                      {!pricesSet
                        ? t('mobile.book.priceNotSet')
                        : total > 0
                          ? `${total} kr`
                          : t('mobile.activities.free')}
                    </div>
                    <button
                      onClick={() => setPick(null)}
                      className="text-[11px] opacity-60 underline"
                      aria-label={t('mobile.common.cancel')}
                    >
                      {t('mobile.common.cancel')}
                    </button>
                  </div>
                </div>
                {/* ── EQUIPMENT (#911 slice 2) ─────────────────────────────
                    Offered only where the venue HAS equipment: a club with
                    none sees the sheet exactly as before. A group of ordinary
                    checkboxes inside a named fieldset, so each machine is
                    announced with its price and the heading that explains it. */}
                {assets.length > 0 && (
                  <fieldset className="mt-3 min-w-0">
                    <legend className="font-mono text-[10px] uppercase tracking-wider opacity-80 mb-1">
                      {t('mobile.book.equipment.heading')}
                    </legend>
                    {freeEquipment.length === 0 ? (
                      <p className="text-[12px] opacity-80">
                        {t('mobile.book.equipment.noneFree')}
                      </p>
                    ) : (
                      <>
                        {freeEquipment.map((a) => {
                          const p = equipmentPrice(a);
                          return (
                            <label
                              key={a.id}
                              className="flex items-center gap-2 py-1 text-[12px] cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={chosenAssets.includes(a.id)}
                                onChange={(e) =>
                                  setChosenAssets((list) =>
                                    e.target.checked
                                      ? [...list, a.id]
                                      : list.filter((id) => id !== a.id),
                                  )
                                }
                                className="accent-lime-500"
                              />
                              <span className="min-w-0 flex-1 font-semibold truncate">
                                {a.name}
                              </span>
                              <span className="tabular-nums">
                                {p > 0 ? `+${p} kr` : t('mobile.book.freeLower')}
                              </span>
                            </label>
                          );
                        })}
                        <p className="text-[11px] opacity-80 mt-0.5">
                          {t('mobile.book.equipment.hint')}
                        </p>
                      </>
                    )}
                  </fieldset>
                )}
                {onRegisterMatch && (
                  <label className="flex items-start gap-2 mt-3 text-[12px] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={registerMatch}
                      onChange={(e) => {
                        setRegisterMatch(e.target.checked);
                        if (e.target.checked && levelMin == null && levelMax == null)
                          applyRecommended();
                      }}
                      className="mt-0.5 accent-lime-500"
                    />
                    {/* The label's text one level up (#900): nested two spans deep
                        it was out of reach of the check that a label actually
                        carries text. Same boxes — the title inline and bold, the
                        hint a block line under it in the sheet's normal weight. */}
                    <span className="font-semibold">
                      {t('mobile.book.registerMatch')}
                      <span className="block opacity-70 font-normal">
                        {t('mobile.book.registerMatchHint')}
                      </span>
                    </span>
                  </label>
                )}
                {onRegisterMatch && registerMatch && (
                  /* A NAMED GROUP, so «Min» and «Maks» are read inside the
                     heading that gives them meaning. They used to carry an
                     `aria-label` that REPLACED the visible word with «Laveste
                     nivå» — a WCAG 2.5.3 label-in-name mismatch, so speech
                     control could not act on what the member could read. The
                     wording is added to the visible label now, not swapped
                     for it. */
                  <div
                    className="mt-2 rounded-xl px-3 py-2"
                    style={{ background: 'rgba(255,255,255,0.08)' }}
                    role="group"
                    aria-labelledby={levelGateId}
                  >
                    <div
                      id={levelGateId}
                      className="font-mono text-[10px] uppercase tracking-wider opacity-70 mb-1"
                    >
                      {t('mobile.book.levelGateHeading', {
                        min: String(recMin),
                        max: String(recMax),
                      })}
                    </div>
                    <div className="flex items-center gap-2 text-[12px]">
                      <label className="flex items-center gap-1">
                        <span className="opacity-70">{t('mobile.book.levelMin')}</span>
                        <select
                          value={levelMin ?? ''}
                          onChange={(e) =>
                            setLevelMin(e.target.value === '' ? null : Number(e.target.value))
                          }
                          className="rounded-lg px-2 py-1 text-[12px] font-semibold"
                          style={{ background: '#fff', color: '#0f1611' }}
                          aria-label={`${t('mobile.book.levelMin')} – ${t('mobile.book.levelMinAria')}`}
                        >
                          <option value="">{t('mobile.book.anyLength')}</option>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                            <option key={n} value={n}>
                              N{n}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="flex items-center gap-1">
                        <span className="opacity-70">{t('mobile.book.levelMax')}</span>
                        <select
                          value={levelMax ?? ''}
                          onChange={(e) =>
                            setLevelMax(e.target.value === '' ? null : Number(e.target.value))
                          }
                          className="rounded-lg px-2 py-1 text-[12px] font-semibold"
                          style={{ background: '#fff', color: '#0f1611' }}
                          aria-label={`${t('mobile.book.levelMax')} – ${t('mobile.book.levelMaxAria')}`}
                        >
                          <option value="">{t('mobile.book.anyLength')}</option>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                            <option key={n} value={n}>
                              N{n}
                            </option>
                          ))}
                        </select>
                      </label>
                      <span className="opacity-60 text-[11px]">
                        {levelMin == null && levelMax == null
                          ? t('mobile.activities.allLevels')
                          : levelMin === recMin && levelMax === recMax
                            ? t('mobile.book.levelRecommended', {
                                min: String(levelMin),
                                max: String(levelMax),
                              })
                            : `N${levelMin ?? 1}–N${levelMax ?? 9}`}
                      </span>
                      {!(levelMin === recMin && levelMax === recMax) && (
                        <button
                          onClick={applyRecommended}
                          className="text-[11px] underline opacity-80"
                          type="button"
                        >
                          {t('mobile.book.useRecommended')}
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {error && (
                  <div
                    // Announced (#1145): a failed booking said nothing to a
                    // screen reader, and nothing at all when it threw.
                    role="alert"
                    className="rounded-xl px-3 py-2 text-[12px] mt-2"
                    style={{ background: '#ffe4e6', color: '#be123c' }}
                  >
                    {error}
                  </div>
                )}
                {!pricesSet && (
                  <p className="text-[12px] opacity-80 mt-3">{t('mobile.book.priceNotSetHint')}</p>
                )}
                <button
                  onClick={confirm}
                  disabled={busy || !pricesSet}
                  className="w-full rounded-2xl font-semibold text-[14px] py-3 mt-3 active:scale-[0.98] transition-transform disabled:opacity-40"
                  style={{ background: '#c2f02f', color: '#0f1611' }}
                >
                  {busy
                    ? t('mobile.book.booking')
                    : total > 0
                      ? t('mobile.book.payAndBook', { price: String(total) })
                      : t('mobile.book.bookIt')}
                </button>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Duration track ──────────────────────────────────────────────────
// A small timeline from the chosen start: one segment per allowed duration,
// sized by the minutes it adds (60 → 90 → 120), lime where that length is
// bookable on this court and hatched where it is not. Tapping a lime segment
// picks that length. End time under each boundary, price inside.
function DurationTrack({
  durations,
  available,
  startTime,
  chosen,
  endTimeFor,
  priceFor,
  onPick,
}: {
  durations: number[];
  available: number[];
  startTime: string;
  chosen: number | null;
  endTimeFor: (d: number) => string | null;
  /** Null when the club has not set its prices (#1312): no price is shown. */
  priceFor: (d: number) => number | null;
  onPick: (d: number) => void;
}) {
  const t = useT();
  const sorted = [...durations].sort((a, b) => a - b);
  let prev = 0;
  const segs = sorted.map((d) => {
    const w = d - prev;
    prev = d;
    return { d, w };
  });
  return (
    // A CLICK BARRIER, NOT A CONTROL (#900): the length buttons live inside a
    // slot card that picks the slot on click, and a tap on a gap or a disabled
    // length must not do that. `role="presentation"` says this box has nothing
    // to announce; the buttons inside are the controls.
    <div className="mt-2.5" role="presentation" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between font-mono text-[9px] text-ink-3 mb-1">
        <span>{t('mobile.book.fromTime', { time: startTime })}</span>
        <span>{t('mobile.book.tapLength')}</span>
      </div>
      <div className="flex gap-0.5" style={{ height: 30 }}>
        {segs.map(({ d, w }) => {
          const ok = available.includes(d);
          const active = chosen === d;
          const p = ok ? priceFor(d) : 0;
          return (
            <button
              key={d}
              disabled={!ok}
              onClick={() => onPick(d)}
              aria-label={
                ok
                  ? t('mobile.book.minutesUntil', { minutes: d, end: endTimeFor(d) ?? '' })
                  : t('mobile.book.minutesUnavailable', { minutes: d })
              }
              className="flex flex-col items-center justify-center rounded-lg leading-none disabled:cursor-not-allowed active:scale-[0.97] transition-transform"
              style={{
                flex: w,
                minWidth: 0,
                background: active
                  ? '#0f1611'
                  : ok
                    ? '#c2f02f'
                    : 'repeating-linear-gradient(135deg, #eef0ea 0 4px, #fff 4px 8px)',
                border: `1px solid ${active ? '#0f1611' : ok ? '#a3d117' : '#e7eae3'}`,
                color: active ? '#fff' : ok ? '#0f1611' : 'var(--ink-3)',
              }}
            >
              <span className="font-mono text-[11px] font-bold">{d}</span>
              {ok && p !== null && (
                <span className="text-[9px]" style={{ opacity: 0.75 }}>
                  {p > 0 ? `${p} kr` : t('mobile.book.freeLower')}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div className="flex gap-0.5 font-mono text-[9px] text-ink-3 mt-0.5">
        {segs.map(({ d, w }) => (
          <span key={d} className="text-right" style={{ flex: w, minWidth: 0 }}>
            {endTimeFor(d) || '·'}
          </span>
        ))}
      </div>
    </div>
  );
}
